import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Header from 'src/layouts/Header';
import Footer from 'src/layouts/Footer';
import useSEO from 'src/hooks/useSEO';
import { NotificationManager } from 'react-notifications';
import axios from 'src/api/axios';

const Contact = () => {
  const auth = useSelector((state) => state.auth);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true';

  // Load reCAPTCHA script
  const loadRecaptcha = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(resolve);
        return;
      }
      
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          window.grecaptcha.ready(resolve);
        });
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.onload = () => {
        window.grecaptcha.ready(resolve);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  useEffect(() => {
    if (recaptchaEnabled) {
      loadRecaptcha()
        .then(() => setRecaptchaLoaded(true))
        .catch((error) => {
          console.error('Error loading reCAPTCHA:', error);
        });
    }
  }, [loadRecaptcha, recaptchaEnabled]);
  
  useSEO({
    title: 'Contact Us - SphynxPlace.com',
    description: 'Get in touch with SphynxPlace.com. Contact us for support, questions, or feedback about our Sphynx cat marketplace.',
    keywords: 'contact SphynxPlace, customer support, help',
    robots: 'noindex, follow',
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  // Auto-fill name and email if user is logged in
  useEffect(() => {
    if (auth.isLoggedIn && auth.user) {
      const fullName = auth.user.first_name && auth.user.last_name 
        ? `${auth.user.first_name} ${auth.user.last_name}`.trim()
        : auth.user.profile_name || '';
      
      setFormData(prev => ({
        ...prev,
        name: fullName || prev.name,
        email: auth.user.email || prev.email,
      }));
    }
  }, [auth.isLoggedIn, auth.user]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    try {
      let requestData = { ...formData };

      // Execute reCAPTCHA only if enabled
      if (recaptchaEnabled) {
        if (!window.grecaptcha) {
          await loadRecaptcha();
        }
        
        const recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
          action: 'contact'
        });
        requestData.recaptcha_token = recaptchaToken;
      }

      const response = await axios.post('api/contact', requestData, {
        disableNotification: true
      });

      NotificationManager.success(response.data.message || 'Thank you for contacting us! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      console.error('Error response:', error.response?.data);

      if (!error.response) {
        NotificationManager.error('No server response');
      } else if (error.response?.status === 400 || error.response?.status === 422) {
        const errorData = error.response.data;
        const validationErrors = errorData.error || errorData.errors || {};

        if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
          const formattedErrors = {};

          for (const [key, value] of Object.entries(validationErrors)) {
            if (Array.isArray(value) && value.length > 0) {
              formattedErrors[key] = typeof value[0] === 'string' ? value[0] : String(value[0]);
            } else if (typeof value === 'string') {
              formattedErrors[key] = value;
            }
          }

          setErrors(formattedErrors);
          NotificationManager.error('Please correct the errors in the form');
        } else if (errorData.error) {
          NotificationManager.error(String(errorData.error));
        } else if (errorData.message) {
          NotificationManager.error(String(errorData.message));
        } else {
          NotificationManager.error('Failed to send message. Please try again.');
        }
      } else {
        NotificationManager.error(error.message || 'An error occurred');
        if (error.response?.data?.message) {
          NotificationManager.error(error.response.data.message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
            <p className="text-lg text-gray-600">
              Have a question or need help? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 text-[#832EE6] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email
                  </h3>
                  <a href="mailto:support@sphynxplace.com" className="text-[#832EE6] hover:text-purple-800">
                    support@sphynxplace.com
                  </a>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 text-[#832EE6] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Response Time
                  </h3>
                  <p className="text-gray-600">We typically respond within 24-48 hours</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a 
                      href="https://www.facebook.com/groups/1133285998276992" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#832EE6] transition-colors"
                      aria-label="Facebook"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://www.instagram.com/sphynxplace/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#832EE6] transition-colors"
                      aria-label="Instagram"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.98-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.98-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-3 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="John Doe"
                    required
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-3 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="john@example.com"
                    required
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-3 ${errors.subject ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="How can we help?"
                    required
                    disabled={isSubmitting}
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-xs mt-1">{errors.subject}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-3 resize-none ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Tell us more about your question or concern..."
                    required
                    disabled={isSubmitting}
                  />
                  {errors.message && (
                    <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-[#832EE6] hover:bg-purple-800 text-white px-6 py-3 text-lg font-bold rounded-[27px] transition-colors duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
